import EpiApiClient from "@/services/serviceClients/epiApiClient";
import createEstimatorService from "@/services/createEstimatorService";
import productCatalogService from "@/services/productCatalogService";
import { 
    ESTIMATE_API_PATH,
    ESTIMATOR_LIST_API_PATH,
    ESTIMATOR_SETTINGS_API_PATH,
    ESTIMATOR_TESTIMONIAL_API_PATH,
    ESTIMATOR_TEMPLATES_API_PATH,
    ESTIMATOR_TOC_API_PATH,
    ESTIMATOR_GENERATE_PROPOSAL_ID_API_PATH,
    ESTIMATOR_GENERATE_PROPOSAL_PDF_API_PATH,
    ESTIMATOR_GENERATE_AI_EMAIL_BODY_API_PATH,
    ESTIMATOR_PROPOSAL_DATA_API_PATH,
    ESTIMATOR_SELL_SHEETS_API_PATH,
    ESTIMATOR_SELL_SHEET_PAGES_API_PATH
 } from "@/config/constants";

class estimatorService {
    static async getEstimateImageProducts(estimateId) {
        try {
            const response = await createEstimatorService.getEstimateById(estimateId);
            const estimate = response.data?.Data?.Estimate;
            if (!response.success || response.data?.ErrorCode !== 0 || !estimate) {
                throw new Error(response.error || response.data?.ErrorMessage || 'Unable to load estimate image context');
            }
            const ids = [...new Set((estimate.EstimateMaterialCosts || [])
                .filter(item => item.Type === 'Standard' && item.ProductID)
                .map(item => item.ProductID))];
            if (!ids.length) return { success: true, data: [], error: null };
            if (!estimate.BranchCode) throw new Error('Estimate branch is missing');

            const products = await productCatalogService.getProductsByIds(estimate.BranchCode, ids);
            if (!products?.success || !Array.isArray(products.data)) {
                throw new Error(products?.error || 'Unable to load estimate variant images');
            }
            return { success: true, data: products.data, error: null };
        } catch (error) {
            console.error('estimatorService.getEstimateImageProducts failed:', error);
            return { success: false, data: null, error: error.message };
        }
    }

    static getSellSheets() {
        return EpiApiClient
            .get(ESTIMATOR_SELL_SHEETS_API_PATH)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                const errorMessage = error.response?.data?.error_description || error.response?.data || error.message || "Request failed";
                return { success: false, error: errorMessage };
            });
    }

    static getSellSheetPages(publicIds) {
        return EpiApiClient
            .post(ESTIMATOR_SELL_SHEET_PAGES_API_PATH, publicIds)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                const errorMessage = error.response?.data?.error_description || error.response?.data || error.message || "Request failed";
                return { success: false, error: errorMessage };
            });
    }

    static postEstimatorConvertEstimateToOrder(payload) {
      return EpiApiClient
        .post("/api/estimator/estimate/convert-to-order/", payload)
        .then(function (response) {
            return { success: true, data: response.data };
        })
        .catch(function (error) {
            const errorMessage = error.response?.data?.error_description || error.response?.data || error.message || "Request failed";
            return { success: false, error: errorMessage };
        });
    }

    static getEstimatesList(payload) {
        return EpiApiClient
            .post(ESTIMATOR_LIST_API_PATH, payload)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
        }
        
    static deleteEstimate(estimateId) {
        const url = `${ESTIMATE_API_PATH}/${estimateId}`;
        return EpiApiClient
            .delete(url)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    static updateEstimate(payload) {
        return EpiApiClient
            .put(ESTIMATE_API_PATH, payload)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    // Generate Proposal ID
    static generateProposalId(payload) {
        return EpiApiClient
            .post(ESTIMATOR_GENERATE_PROPOSAL_ID_API_PATH, payload)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }
    static generatePdfFromProposal(proposalId) {
        const url = `${ESTIMATOR_GENERATE_PROPOSAL_PDF_API_PATH}?proposalId=${encodeURIComponent(proposalId)}`;
        return EpiApiClient
            .post(url, null, { responseType: 'blob' })
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }
    static generateAIEmailBody(payload) {
        return EpiApiClient
            .post(ESTIMATOR_GENERATE_AI_EMAIL_BODY_API_PATH, payload)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    // Estimator Settings APIs
    static getEstimatorSettings() {
        return EpiApiClient
            .get(ESTIMATOR_SETTINGS_API_PATH)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }
    
    static getEstimatorTestimonials() {
        return EpiApiClient
            .get(ESTIMATOR_TESTIMONIAL_API_PATH)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }
    
    static updateEstimatorTestimonials(payload) {
        return EpiApiClient
            .post(ESTIMATOR_TESTIMONIAL_API_PATH, payload)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    static async saveEstimatorSettings(payload) {
        try {
            const response = await EpiApiClient.post(ESTIMATOR_SETTINGS_API_PATH, payload);
            const isSuccess = response.data === true || response.data === "true" || response.status === 200;
            
            return isSuccess 
                ? { success: true, data: response.data, error: null }
                : { success: false, data: null, error: "Failed to save settings" };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description || error.message || "Request failed";
            return { success: false, data: null, error: errorMessage };
        }
    }

    static deleteEstimatorTestimonial(testimonialId) {
        const url = `${ESTIMATOR_TESTIMONIAL_API_PATH}/${testimonialId}`;
        return EpiApiClient
            .delete(url)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    // Template APIs - using existing customer service endpoints
    static getTemplates(branchCode = '') {
        const url = branchCode ? `${ESTIMATOR_TEMPLATES_API_PATH}/${branchCode}` : ESTIMATOR_TEMPLATES_API_PATH;
        return EpiApiClient
            .get(url)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    // Get tier settings from estimator settings
    static getTierSettings() {
        return EpiApiClient
            .get(ESTIMATOR_SETTINGS_API_PATH)
            .then(function (response) {
                return { success: true, data: response.data };
            })
            .catch(function (error) {
                return { success: false, error: error.response.data.error_description };
            });
    }

    // Terms and Conditions (TOC) APIs
    // Check if user has accepted Terms and Conditions
    // Response: { ErrorCode: 0, Data: null/true/false, ErrorMessage: "", CacheVersion: "..." }
    // Data meanings:
    //   null  → Fresh user (never accepted or declined)
    //   true  → User has accepted
    //   false → User has declined
    static async checkTOCStatus() {
        try {
            const response = await EpiApiClient.get(ESTIMATOR_TOC_API_PATH);
            const isSuccess = response.data?.ErrorCode === 0;
            
            if (isSuccess) {
                return {
                    success: true,
                    data: response.data.Data,  // Return actual value: null/true/false
                    error: null
                };
            }
            
            return {
                success: false,
                data: null,
                error: response.data?.ErrorMessage || "Failed to check TOC status"
            };
        } catch (error) {
            const errorMessage = error.response?.data?.ErrorMessage || error.message || "Failed to check TOC status";
            console.error('estimatorService.checkTOCStatus failed:', errorMessage, error);
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }

    // Save user's Terms and Conditions acceptance/rejection
    // Response: { ErrorCode: 0, Data: true, ErrorMessage: "", CacheVersion: "..." }
    static async setTOCResponse(payload) {
        try {
            const response = await EpiApiClient.post(ESTIMATOR_TOC_API_PATH, payload);
            const isSuccess = response.data?.ErrorCode === 0;
            
            if (isSuccess) {
                return {
                    success: true,
                    data: {
                        saved: response.data.Data === true
                    },
                    error: null
                };
            }
            
            return {
                success: false,
                data: null,
                error: response.data?.ErrorMessage || "Failed to save TOC response"
            };
        } catch (error) {
            const errorMessage = error.response?.data?.ErrorMessage || error.message || "Failed to save TOC response";
            console.error('estimatorService.setTOCResponse failed:', errorMessage, error);
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }

    // Get proposal data by proposal code (for public proposal URL view)
    static async getProposalData(proposalCode) {
        try {
            const url = `${ESTIMATOR_PROPOSAL_DATA_API_PATH}/${proposalCode}`;
            const response = await EpiApiClient.get(url);
            return {
                success: true,
                data: response.data,
                error: null
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description || error.message || "Failed to fetch proposal data";
            console.error('estimatorService.getProposalData failed:', errorMessage, error);
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }
}

export default estimatorService;